import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { TooltipProps } from '../../types/components';

/**
 * Tooltip component that renders content in a portal for proper stacking
 * 
 * This component creates a tooltip that appears on hover and is rendered
 * directly to the document body using a React Portal to avoid stacking
 * context and overflow issues.
 */
const Tooltip: React.FC<TooltipProps> = ({ 
  title, 
  content, 
  position = 'top', 
  children 
}) => {
  // State for tracking hover status
  const [isHovered, setIsHovered] = useState(false);
  
  // State for tooltip position
  const [tooltipCoordinates, setTooltipCoordinates] = useState({ top: 0, left: 0 });
  
  // Reference to the tooltip container
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Update tooltip position when hover state changes
  useEffect(() => {
    if (isHovered && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      
      // Set position based on the chosen direction
      switch(position) {
        case 'bottom':
          setTooltipCoordinates({
            top: rect.bottom + window.scrollY + 15,
            left: rect.left + rect.width / 2 + window.scrollX
          });
          break;
        case 'left':
          setTooltipCoordinates({
            top: rect.top + rect.height / 2 + window.scrollY,
            left: rect.left - 15 + window.scrollX
          });
          break;
        case 'right':
          setTooltipCoordinates({
            top: rect.top + rect.height / 2 + window.scrollY,
            left: rect.right + 15 + window.scrollX
          });
          break;
        case 'top':
        default:
          setTooltipCoordinates({
            top: rect.top + window.scrollY - 15,
            left: rect.left + rect.width / 2 + window.scrollX
          });
          break;
      }
    }
  }, [isHovered, position]);
  
  // ID for accessibility
  const tooltipId = useRef(`tooltip-${Math.random().toString(36).substr(2, 9)}`);
  
  // Determine arrow styles based on position
  const getArrowStyles = () => {
    switch(position) {
      case 'bottom':
        return {
          position: 'absolute',
          left: '50%',
          marginLeft: '-8px',
          borderWidth: '0 8px 8px 8px',
          borderStyle: 'solid',
          borderColor: 'transparent transparent #ffffff transparent',
          top: '-8px'
        };
      case 'left':
        return {
          position: 'absolute',
          top: '50%',
          marginTop: '-8px',
          borderWidth: '8px 0 8px 8px',
          borderStyle: 'solid',
          borderColor: 'transparent transparent transparent #ffffff',
          right: '-8px'
        };
      case 'right':
        return {
          position: 'absolute',
          top: '50%',
          marginTop: '-8px',
          borderWidth: '8px 8px 8px 0',
          borderStyle: 'solid',
          borderColor: 'transparent #ffffff transparent transparent',
          left: '-8px'
        };
      case 'top':
      default:
        return {
          position: 'absolute',
          left: '50%',
          marginLeft: '-8px',
          borderWidth: '8px 8px 0 8px',
          borderStyle: 'solid',
          borderColor: '#ffffff transparent transparent transparent',
          bottom: '-8px'
        };
    }
  };

  return (
    <div 
      className="tooltip-container inline-flex" 
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      aria-describedby={tooltipId.current}
    >
      {/* Wrapped children with accessibility attributes */}
      <div
        role="button"
        tabIndex={0}
        className="tooltip-trigger outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 rounded"
      >
        {children}
      </div>
      
      {/* Portal for tooltip content */}
      {isHovered && ReactDOM.createPortal(
        <div 
          className="tooltip-portal" 
          style={{ pointerEvents: 'none' }}
          role="tooltip"
          id={tooltipId.current}
        >
          <div 
            style={{
              visibility: 'visible',
              opacity: 1,
              position: 'absolute',
              top: position === 'top' ? tooltipCoordinates.top - 50 : tooltipCoordinates.top,
              left: tooltipCoordinates.left,
              transform: position === 'left' ? 'translateX(-100%) translateY(-50%)' : 
                        position === 'right' ? 'translateY(-50%)' : 'translateX(-50%)',
              zIndex: 1000000,
              backgroundColor: '#ffffff',
              color: '#333333',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '12px',
              width: '280px',
              maxWidth: '90vw',
              textAlign: 'left',
              backdropFilter: 'none',
              background: '#ffffff'
            }}
          >
            {title && (
              <div 
                className="tooltip-title" 
                style={{
                  fontWeight: 600,
                  marginBottom: '8px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid #e2e8f0',
                  color: '#3b82f6'
                }}
              >
                {title}
              </div>
            )}
            
            <div 
              className="tooltip-content" 
              style={{
                fontSize: '0.875rem',
                lineHeight: 1.5,
                maxHeight: '200px',
                overflowY: 'auto'
              }}
            >
              {content}
            </div>
            
            {/* Arrow element with position-specific styles */}
            <div 
              className="tooltip-arrow"
              style={getArrowStyles() as React.CSSProperties}
            ></div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;